import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Outlet, useParams } from "react-router-dom";
import * as yup from "yup";
import type {
  EditableHousehold,
  FeeChargeLocation,
} from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import Loading from "../../Loading";
import FormError from "../../components/FormError";
import SettlementWarning from "../../components/SettlementWarning";
import TabContainerWithTabs from "../../components/TabContainer";
import {
  deserializeCurrentAndTargetPercents,
  useAuthenticatedPercentFetch,
} from "../percents";
import {
  idOptionalSelectorSchema,
  optionalStringValueTransform,
  securityRestrictionSchema,
} from "../../lib/forms";
import HouseholdHeader from "./HouseholdHeader";
import HouseholdNav from "./HouseholdNav";
import {
  type HouseholdWithAccountsAndRiskAllocation,
  useQueryHousehold,
} from "./lib";

const feeChargeLocations: FeeChargeLocation[] = [
  "Custodian Billed",
  "Direct Billed",
];

// The api sends these fields as explicit null rather than omitting them
// (APP-1535). yup 1.x refuses a null against a bare schema, so the resolver
// would reject every household that has one and handleSubmit would never
// fire. An empty control still means "none"; it now reads as null instead
// of an absent key, and the route writes the same NULL.
const nullableIdSchema = yup
  .number()
  .transform((value, originalValue) =>
    typeof originalValue === "undefined" ||
    originalValue === null ||
    originalValue === ""
      ? null
      : value,
  )
  .defined()
  .nullable()
  .default(null);

const schema: yup.ObjectSchema<EditableHousehold> = yup.object({
  assignedModelId: nullableIdSchema.label("Assigned Model"),
  rebalanceWeightLimit: nullableIdSchema
    .min(0)
    .max(100)
    .label("Rebalance Weight Limit"),
  toleranceRiskPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Risk Tolerance"),
  toleranceAssetClassPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Asset Class Tolerance"),
  toleranceSecurityPercent: yup
    .number()
    .min(0)
    .max(100)
    .nullable()
    .defined()
    .label("Security Tolerance"),
  rebalancePeriod: yup
    .string()
    .required()
    .oneOf(["d", "w", "m", "q", "y"])
    .label("Rebalance Recurrence"),
  rebalancePeriodStartDate: yup
    .date()
    .required()
    .label("Rebalance Recurrence Start Date"),
  feeStructureId: nullableIdSchema.label("Assigned Fee Structure"),
  billingMinimumId: nullableIdSchema.label("Assigned Billing Minimum"),
  feeChargeLocation: yup
    .string()
    .oneOf<FeeChargeLocation>(feeChargeLocations)
    .transform(optionalStringValueTransform)
    .label("Fee Charge Location"),
  securityRestrictions: securityRestrictionSchema.required(),
  capitalGainsBudgetST: idOptionalSelectorSchema.label(
    "Capital Gains Budget (Short-Term)",
  ),
  capitalGainsBudgetLT: idOptionalSelectorSchema.label(
    "Capital Gains Budget (Long-Term)",
  ),
});

export const HouseholdContext = React.createContext<{
  household?: HouseholdWithAccountsAndRiskAllocation;
  schema: yup.ObjectSchema<EditableHousehold>;
}>({ schema });

const HouseholdInfo = () => {
  const { householdId } = useParams();

  const {
    isPending: isPendingHousehold,
    isError: isErrorHousehold,
    data: dataHousehold,
  } = useQueryHousehold(parseInt(householdId ?? ""), {
    includeAccounts: true,
    includeCRM: true,
  });

  const { data: householdRiskAllocationData } = useAuthenticatedPercentFetch<
    UnpackResponse<HouseholdsController["riskAllocation"]>
  >(`/v1/households/${householdId}/risk-allocation`, (data) => ({
    riskAllocation: deserializeCurrentAndTargetPercents(data.riskAllocation),
    accounts: Object.fromEntries(
      Object.entries(data.accounts).map(([accountId, allocation]) => [
        accountId,
        deserializeCurrentAndTargetPercents(allocation),
      ]),
    ),
  }));

  const household: HouseholdWithAccountsAndRiskAllocation | undefined = useMemo(
    () =>
      typeof dataHousehold === "undefined"
        ? undefined
        : {
            ...dataHousehold,
            householdBalance: dataHousehold.householdBalance || 0, // provide a default value
            accounts: (dataHousehold.accounts ?? []).map((account) => ({
              ...account,
              riskAllocation:
                householdRiskAllocationData?.data.accounts[account.id],
            })),
            riskAllocation: householdRiskAllocationData?.data
              ?.riskAllocation ?? {
              current: {},
              target: {},
            },
          },
    [dataHousehold, householdRiskAllocationData],
  );

  const form = useForm<EditableHousehold>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (typeof household !== "undefined" && !isPendingHousehold) {
      form.reset(household);
    }
  }, [household, isPendingHousehold, form]);

  return isPendingHousehold ? (
    <Loading />
  ) : isErrorHousehold ? (
    <FormError message="Failed to load household" />
  ) : !household ? (
    <FormError message="Household not found" />
  ) : (
    <HouseholdContext.Provider value={{ household, schema }}>
      <HouseholdHeader />
      <Row className="mb-3">
        <Col>
          <SettlementWarning />
        </Col>
      </Row>
      <FormProvider {...form}>
        <Row>
          <Col>
            <TabContainerWithTabs tabs={HouseholdNav}>
              <Col>
                <Outlet />
              </Col>
            </TabContainerWithTabs>
          </Col>
        </Row>
      </FormProvider>
    </HouseholdContext.Provider>
  );
};

export default HouseholdInfo;
